import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { PackageQR } from "@/components/PackageQR";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const RELS = ["SELF", "PARENT", "SPOUSE", "CHILD"];
const EMPTY_PKG = { name: "", slug: "", description: "", image_url: "", price: 0, currency: "PHP", validity_value: 12, validity_unit: "MONTHS",
  eligibility_requirements: "", activation_requirements: "", terms: "", status: "DRAFT", provider_ids: [] };
const EMPTY_BEN = { name: "", code: "", description: "", benefit_type: "SERVICE", service_kind: "", delivery_mode: "ONLINE", quantity: 1,
  eligible_relationships: ["SELF"], requires_booking: true, provider_ids: [], insurer_name: "", sort_order: 0, active: true };

function ProviderPicker({ value, onChange }) {
  const { data } = useFetch("/admin/providers");
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="flex flex-wrap gap-2">
      {data?.map((p) => (
        <label key={p.id} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${value.includes(p.id) ? "border-purple-500 bg-purple-50 text-purple-800" : "border-slate-200"}`}>
          <input type="checkbox" checked={value.includes(p.id)} onChange={() => toggle(p.id)} data-testid={`provider-check-${p.id}`} /> {p.name}
        </label>
      ))}
      {data?.length === 0 && <span className="text-xs text-slate-400">No providers yet</span>}
    </div>
  );
}

export function PackageForm({ initial, onSaved }) {
  const [f, setF] = useState({ ...EMPTY_PKG, ...initial, image_url: initial?.image_url || "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, num) => (e) => setF({ ...f, [k]: num ? e.target.value === "" ? "" : Number(e.target.value) : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const body = { ...f, image_url: f.image_url || null, slug: f.slug || null };
    try {
      const { data } = initial?.id ? await api.put(`/admin/packages/${initial.id}`, body) : await api.post("/admin/packages", body);
      toast.success("Package saved");
      onSaved(data);
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="package-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Package name"><input className="field" value={f.name} onChange={set("name")} required data-testid="package-name-input" /></Field>
        <Field label="URL slug" hint="Used in the standard QR link /enroll/slug"><input className="field font-mono" value={f.slug} onChange={set("slug")} placeholder="auto from name" data-testid="package-slug-input" /></Field>
      </div>
      <Field label="Description"><textarea className="field" rows={3} value={f.description} onChange={set("description")} data-testid="package-description-input" /></Field>
      <div className="grid gap-4 sm:grid-cols-4">
        <Field label="Price"><input className="field" type="number" min="0" step="0.01" value={f.price} onChange={set("price", true)} required data-testid="package-price-input" /></Field>
        <Field label="Currency"><input className="field uppercase" maxLength={3} value={f.currency} onChange={set("currency")} data-testid="package-currency-input" /></Field>
        <Field label="Validity"><input className="field" type="number" min="1" value={f.validity_value} onChange={set("validity_value", true)} required data-testid="package-validity-input" /></Field>
        <Field label="Unit">
          <select className="field" value={f.validity_unit} onChange={set("validity_unit")} data-testid="package-validity-unit-select"><option value="MONTHS">Months</option><option value="DAYS">Days</option></select>
        </Field>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Status">
          <select className="field" value={f.status} onChange={set("status")} data-testid="package-status-select">
            {["DRAFT", "ACTIVE", "INACTIVE", "ARCHIVED"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <Field label="Image URL (optional)"><input className="field" value={f.image_url} onChange={set("image_url")} data-testid="package-image-input" /></Field>
      </div>
      <Field label="Eligibility requirements"><textarea className="field" rows={2} value={f.eligibility_requirements} onChange={set("eligibility_requirements")} data-testid="package-eligibility-input" /></Field>
      <Field label="Activation requirements"><textarea className="field" rows={2} value={f.activation_requirements} onChange={set("activation_requirements")} data-testid="package-activation-input" /></Field>
      <Field label="Terms"><textarea className="field" rows={2} value={f.terms} onChange={set("terms")} data-testid="package-terms-input" /></Field>
      <Field label="Assigned providers"><ProviderPicker value={f.provider_ids} onChange={(v) => setF({ ...f, provider_ids: v })} /></Field>
      <ErrorBox error={error} />
      <button className="btn-primary w-full" disabled={busy} data-testid="package-save-button">Save package</button>
    </form>
  );
}

function BenefitForm({ pkgId, initial, onSaved }) {
  const [f, setF] = useState({ ...EMPTY_BEN, ...initial, service_kind: initial?.service_kind || "", insurer_name: initial?.insurer_name || "" });
  const [unlimited, setUnlimited] = useState(initial ? initial.quantity == null : false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleRel = (r) => setF({ ...f, eligible_relationships: f.eligible_relationships.includes(r) ? f.eligible_relationships.filter((x) => x !== r) : [...f.eligible_relationships, r] });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...f, quantity: unlimited ? null : Number(f.quantity), sort_order: Number(f.sort_order) || 0, service_kind: f.service_kind || null, insurer_name: f.insurer_name || null };
    try {
      initial?.id ? await api.put(`/admin/benefits/${initial.id}`, body) : await api.post(`/admin/packages/${pkgId}/benefits`, body);
      toast.success("Benefit saved");
      onSaved();
    } catch (err) { setError(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="space-y-4" data-testid="benefit-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Benefit name"><input className="field" value={f.name} onChange={set("name")} required data-testid="benefit-name-input" /></Field>
        <Field label="Code"><input className="field font-mono uppercase" value={f.code} onChange={set("code")} required data-testid="benefit-code-input" /></Field>
      </div>
      <Field label="Description"><textarea className="field" rows={2} value={f.description} onChange={set("description")} data-testid="benefit-description-input" /></Field>
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Type">
          <select className="field" value={f.benefit_type} onChange={(e) => setF({ ...f, benefit_type: e.target.value, requires_booking: e.target.value === "SERVICE" })} data-testid="benefit-type-select">
            <option value="SERVICE">Service</option><option value="INSURANCE">Insurance</option><option value="PERK">Perk</option>
          </select>
        </Field>
        <Field label="Delivery">
          <select className="field" value={f.delivery_mode} onChange={set("delivery_mode")} data-testid="benefit-delivery-select">
            <option value="ONLINE">Online</option><option value="IN_PERSON">In person</option><option value="NONE">None</option>
          </select>
        </Field>
        <Field label="Service kind"><input className="field font-mono" value={f.service_kind} onChange={set("service_kind")} placeholder="ONLINE_CONSULTATION" data-testid="benefit-kind-input" /></Field>
      </div>
      <div className="grid items-end gap-4 sm:grid-cols-3">
        <Field label="Quantity"><input className="field" type="number" min="0" value={unlimited ? "" : f.quantity} disabled={unlimited} onChange={set("quantity")} data-testid="benefit-quantity-input" /></Field>
        <label className="flex items-center gap-2 pb-3 text-sm"><input type="checkbox" checked={unlimited} onChange={(e) => setUnlimited(e.target.checked)} data-testid="benefit-unlimited-check" /> Unlimited</label>
        <label className="flex items-center gap-2 pb-3 text-sm"><input type="checkbox" checked={f.requires_booking} onChange={(e) => setF({ ...f, requires_booking: e.target.checked })} data-testid="benefit-booking-check" /> Bookable</label>
      </div>
      <Field label="Eligible beneficiaries">
        <div className="flex flex-wrap gap-2">{RELS.map((r) => (
          <label key={r} className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold ${f.eligible_relationships.includes(r) ? "border-purple-500 bg-purple-50" : "border-slate-200"}`}>
            <input type="checkbox" checked={f.eligible_relationships.includes(r)} onChange={() => toggleRel(r)} data-testid={`benefit-rel-${r}`} /> {r === "SELF" ? "Cardholder" : r}
          </label>
        ))}</div>
      </Field>
      {f.benefit_type === "INSURANCE" && <Field label="Insurer name"><input className="field" value={f.insurer_name} onChange={set("insurer_name")} data-testid="benefit-insurer-input" /></Field>}
      <Field label="Assigned providers (empty = any active provider)"><ProviderPicker value={f.provider_ids} onChange={(v) => setF({ ...f, provider_ids: v })} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Sort order"><input className="field" type="number" value={f.sort_order} onChange={set("sort_order")} data-testid="benefit-sort-input" /></Field>
        <label className="flex items-center gap-2 pt-6 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="benefit-active-check" /> Active</label>
      </div>
      <ErrorBox error={error} />
      <button className="btn-primary w-full" data-testid="benefit-save-button">Save benefit</button>
    </form>
  );
}

function BenefitsTable({ pkg, onChange }) {
  const [editing, setEditing] = useState(null);
  const disable = async (b) => {
    if (!window.confirm(`Disable "${b.name}"? Existing customer entitlements are not affected.`)) return;
    try { await api.delete(`/admin/benefits/${b.id}`); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section id="benefits" className="surface overflow-x-auto">
      <div className="flex items-center justify-between p-5 pb-3">
        <div><h2 className="text-base font-bold">Package benefits</h2><p className="text-xs text-slate-500">Changes apply to new activations; existing entitlements keep their snapshot.</p></div>
        <button className="btn-primary btn-sm" onClick={() => setEditing({})} data-testid="add-benefit-button"><Plus size={14} /> Add benefit</button>
      </div>
      <table className="data-table" data-testid="benefits-table">
        <thead><tr><th>Benefit</th><th>Type</th><th>Qty</th><th>Eligible</th><th>Status</th><th /></tr></thead>
        <tbody>{pkg.benefits.map((b) => (
          <tr key={b.id} data-testid={`benefit-row-${b.code}`}>
            <td><p className="font-semibold">{b.name}</p><p className="font-mono text-[11px] text-slate-400">{b.code}</p></td>
            <td>{b.benefit_type}</td><td className="font-mono">{b.quantity ?? "∞"}</td>
            <td className="text-xs">{b.eligible_relationships.join(", ")}</td>
            <td><StatusBadge status={b.active ? "ACTIVE" : "INACTIVE"} /></td>
            <td className="whitespace-nowrap text-right">
              <button className="btn-ghost btn-sm" onClick={() => setEditing(b)} data-testid={`edit-benefit-${b.code}`}><Pencil size={13} /></button>
              {b.active && <button className="btn-ghost btn-sm text-red-600" onClick={() => disable(b)} data-testid={`disable-benefit-${b.code}`}><Trash2 size={13} /></button>}
            </td>
          </tr>
        ))}</tbody>
      </table>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit benefit" : "Add benefit"}</DialogTitle></DialogHeader>
          {editing && <BenefitForm pkgId={pkg.id} initial={editing.id ? editing : null} onSaved={() => { setEditing(null); onChange(); }} />}
        </DialogContent>
      </Dialog>
    </section>
  );
}

export default function PackageEditor() {
  const { id } = useParams();
  const { data: pkg, error, loading, reload } = useFetch(`/admin/packages/${id}`);
  const settings = useFetch("/admin/settings");
  if (loading && !pkg) return <Spinner />;
  if (!pkg) return <ErrorBox error={error} />;
  return (
    <div className="space-y-6">
      <Link to="/admin/packages" className="btn-ghost btn-sm -ml-2" data-testid="back-to-packages"><ArrowLeft size={14} /> Packages</Link>
      <PageHead eyebrow="Edit package" title={pkg.name} actions={<StatusBadge status={pkg.status} />} />
      <div className="grid gap-6 xl:grid-cols-[1fr_320px]">
        <div className="surface p-6"><PackageForm key={pkg.updated_at} initial={pkg} onSaved={reload} /></div>
        <div className="surface h-fit p-6">
          <h2 className="mb-1 text-base font-bold">Standard package QR</h2>
          <p className="mb-4 text-xs text-slate-500">One QR for every physical card of this package. Opens the package page — it never activates benefits.</p>
          <PackageQR pkg={pkg} base={settings.data?.public_base_url} />
        </div>
      </div>
      <BenefitsTable pkg={pkg} onChange={reload} />
    </div>
  );
}
